'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/lib/useAuth';
import { ICON_MAP } from '@/components/navigation/NavIconMap';
import {
  BOTTOM_BAR_TABS,
  filterBottomBarTabs,
  filterNavGroups,
  getNavPreset,
  NAV_PRESETS,
} from '@/lib/platform/navigation';
import type { BottomBarTab, NavGroup } from '@/lib/platform/navigation';

function isTabActive(tab: BottomBarTab, pathname: string): boolean {
  if (tab.activeWhen.exact?.includes(pathname)) return true;
  return tab.activeWhen.startsWith?.some((prefix) => pathname.startsWith(prefix)) ?? false;
}

export function DashboardBottomNav() {
  const pathname = usePathname();
  const { user, authEnabled, loading } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  const ctx = { authEnabled, loading, role: user?.role ?? null };

  // Compute "More" groups from the same nav-config the sidebar uses.
  const preset = user ? getNavPreset(user.role) : 'owner';
  const presetGroups: NavGroup[] = !authEnabled
    ? NAV_PRESETS.owner
    : NAV_PRESETS[preset];
  const allGroups = filterNavGroups(presetGroups, ctx);

  // Primary bottom-bar tabs come from the shared config and respect permissions.
  const visibleTabs = filterBottomBarTabs(BOTTOM_BAR_TABS, ctx);

  // Determine active tab
  const activeTab = visibleTabs.find((tab) => isTabActive(tab, pathname));
  const moreActive = !activeTab && !pathname.startsWith('/crew');

  return (
    <>
      {/* Bottom tab bar — phone only */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-zinc-950 border-t border-zinc-800 flex items-stretch md:hidden"
        style={{ height: 56, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {visibleTabs.map((tab) => {
          const isActive = tab === activeTab;
          const Icon = ICON_MAP[tab.icon] ?? MoreHorizontal;
          return (
            <Link
              key={tab.id}
              href={tab.href}
              className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 transition-colors ${
                isActive ? 'text-emerald-400' : 'text-zinc-500'
              }`}
            >
              <Icon className="w-6 h-6" />
              <span className="w-full truncate text-center text-[8.5px] leading-tight max-sm:tracking-tight sm:text-xs uppercase sm:tracking-wide font-medium">
                {tab.label}
              </span>
            </Link>
          );
        })}
        {/* More button */}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 transition-colors ${
            moreActive ? 'text-emerald-400' : 'text-zinc-500'
          }`}
        >
          <MoreHorizontal className="w-6 h-6" />
          <span className="w-full truncate text-center text-[8.5px] leading-tight max-sm:tracking-tight sm:text-xs uppercase sm:tracking-wide font-medium">
            More
          </span>
        </button>
      </nav>

      {/* More sheet — full-screen overlay */}
      {moreOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          onClick={() => setMoreOpen(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-zinc-950 border-t border-zinc-800 rounded-t-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-zinc-950 p-4 border-b border-zinc-800 flex items-center justify-between">
              <span className="text-sm font-bold text-zinc-400 uppercase tracking-wider">
                More
              </span>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                aria-label="Close menu"
                className="p-2 -mr-2 text-zinc-500 hover:text-white transition-colors text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {/* Groups */}
            <div className="p-4 space-y-6">
              {allGroups.map((group) => (
                <div key={group.group}>
                  <h3 className="mb-2 px-1 text-[10px] font-bold tracking-wider text-zinc-500 uppercase">
                    {group.group}
                  </h3>
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = ICON_MAP[item.icon] ?? MoreHorizontal;
                      return (
                        <Link
                          key={item.name}
                          href={item.href}
                          onClick={() => setMoreOpen(false)}
                          className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-zinc-400 hover:bg-white/5 hover:text-white transition-colors"
                        >
                          <Icon className="w-4 h-4 flex-shrink-0 text-zinc-500" />
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
